import { DataGridErrorOverlay } from "../../common-ui/DataGridErrorOverlay";
import {
  ProgramRoleSelectFields,
  UserNetworkErrors
} from "../../app/constants";
import {
  UserNotifications,
  showToastForBulkResult,
  useUsersByProgramIds
} from "./userUtils";
import { useAppSelector } from "../../app/hooks";
import { selectCurrentUser, selectUserRoles } from "./usersSlice";
import { Role } from "../Auth/roles";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { uniqBy } from "lodash";
import {
  getActiveProgramAdminedProgramIds,
  getProgramMembershipsAsProgramAdmin
} from "../../app/utils";
import { UserTableProps } from "../../app/types";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  IconButton,
  InputLabel,
  Link,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  Tooltip,
  Typography
} from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";
import {
  GroupAdd,
  PersonAdd,
  ScheduleSendRounded
} from "@mui/icons-material";
import { useEffect, useState, useRef } from "react";
import { useDispatch } from "react-redux";
import { DataGridPremium, gridClasses } from "@mui/x-data-grid-premium";
import {
  alphabetizePrograms,
  programLabel,
  programsVisibleTo,
  useAllPrograms
} from "../Programs/programUtils";
import {
  Program,
  ProgramMembership,
  ProgramRole
} from "../../API";
import {
  setShowToast,
  setToastAutoHideDuration,
  setToastMessage,
  setToastStyle
} from "../../common-ui/toastSlice";
import {
  applyBulkEnd,
  toEndDateIso,
  BulkEndTarget
} from "./bulkEndExecution";
import EventBusyRounded from "@mui/icons-material/EventBusyRounded";
import CloseRounded from "@mui/icons-material/CloseRounded";
import LoadingButton from "@mui/lab/LoadingButton";
import {
  useBulkResendUserInvitations,
  buildResendSummaryMessage
} from "../UserInvitations/userInvitationUtils";
import {
  applyPgyBumps,
  canBumpUser,
  getPgyBumpPlan,
  useTrainingStages
} from "../TrainingStages/trainingStagesAPI";
import { updateUser, fetchUser } from "./usersAPI";
import {
  setModalBoxStyle,
  setModalContent,
  setModalTitle,
  setShowModal
} from "../../common-ui/modalSlice";
import PgyDeltaInput from "./PgyDeltaInput";
import {
  selectFeatureFlags,
  selectFeatureFlagsError,
  selectFeatureFlagsStatus
} from "../Firebase/firebaseSlice";
import { useNetworkProgramsByPrograms } from "../Networks/networkUtils";
import { ProgramUser } from "../../custom-types";
import { ProgramRoleSelectFieldsNew } from "./SysAdminsUsersTable";

type ProgramAdminsUsersTableProps = UserTableProps;

export const ProgramAdminsUsersTable = ({
  disableVirtualization = false,
  columns,
  handleRowClick
}: ProgramAdminsUsersTableProps) => {
  const { t } = useTranslation();
  const currentUser = useAppSelector(selectCurrentUser);
  const currentUsersRoles = useAppSelector(selectUserRoles);
  const isSysAdmin = !!currentUsersRoles?.includes(Role.SystemAdmin);
  const { programs, status: programsStatus } = useAllPrograms();
  const flags = useAppSelector(selectFeatureFlags);
  const flagsStatus = useAppSelector(selectFeatureFlagsStatus);
  const flagsError = useAppSelector(selectFeatureFlagsError);
  const { allow_bulk_invite } = flags;

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [pgyModalInvocations, setPgyModalInvocations] = useState(0);
  const [pgyBumpValue, setPgyBumpValue] = useState<number | undefined>(1);
  const { trainingStages } = useTrainingStages();

  const [selectedProgramId, setSelectedProgramId] = useState<string>("");
  const [selectedRole, setSelectedRole] = useState<ProgramRole | string>("");
  const [selectedStatus, setSelectedStatus] = useState<string>("");
  const [refetchUsers, setRefetchUsers] = useState<number>(0);

  // SE-1723: PA simpler bulk-end — select users, end their active memberships
  // (in the PA's programs) as of today. No tray, no date picker.
  const [paEndConfirmOpen, setPaEndConfirmOpen] = useState(false);
  const [paEndRunning, setPaEndRunning] = useState(false);
  // membership keys the PA has UNCHECKED in the confirm review (default: all in).
  const [paExcluded, setPaExcluded] = useState<Set<string>>(new Set());
  // Fresh active memberships (in the PA's programs) per user, fetched when the
  // dialog opens — the table data can lag a membership edit.
  const [paFresh, setPaFresh] = useState<
    Record<
      string,
      {
        id: string;
        programId: string;
        programRole: ProgramRole;
        programName: string | null;
      }[]
    >
  >({});
  const [paLoadingMemberships, setPaLoadingMemberships] = useState(false);
  const [allUsersAdminedPrograms, setAllUsersAdminedPrograms] = useState<
    Program[]
  >([]);

  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [resendConfirmOpen, setResendConfirmOpen] = useState(false);

  // Refs to track previous values and prevent unnecessary API calls
  const prevProgramsRef = useRef<Program[]>([]);
  const prevSelectedProgramIdRef = useRef<string>("");
  const prevSelectedRoleRef = useRef<ProgramRole | string>("");
  const prevRefetchUsersRef = useRef<number>(0);
  const prevCurrentUserIdRef = useRef<string | undefined>(undefined);

  const {
    status: resendInvitationsStatus,
    successCount,
    currentStep,
    summary: resendSummary,
    bulkResendUserInvitations
  } = useBulkResendUserInvitations();

  const {
    networkPrograms,
    status: networkProgramsStatus,
    error: networkProgramsError,
    getNetworkProgramsByPrograms
  } = useNetworkProgramsByPrograms();

  // Grab the initial list of users that have memberships to these programs.
  const {
    users,
    status: getUsersStatus,
    error,
    getUsersByProgramIds
  } = useUsersByProgramIds();

  type ProgramUserWithRawRoles = ProgramUser & { rawRoles: ProgramRole[] };

  const enrichUser = (user: ProgramUser): ProgramUserWithRawRoles => {
    const programMemberships = user.programMemberships?.items || [];
    const activeMemberships = programMemberships.filter((m) => !m?.endDate);

    const rawRoles = activeMemberships
      .map((membership) => membership?.programRole)
      .filter((r): r is ProgramRole => !!r); // Type narrowing

    const roleSet = new Set<string>();
    let hasClinician = false;
    rawRoles.forEach((role) => {
      if (role === "Trainee" || role === "Attending") {
        hasClinician = true;
      } else {
        roleSet.add(role);
      }
    });
    if (hasClinician) roleSet.add("Clinician");

    const trainingPrograms = programs
      .filter((p) =>
        activeMemberships.some(
          (m) => m?.programId === p.id && m.programRole === "Trainee"
        )
      )
      .map((p) => p.name)
      .join(", ");

    const institutionName = programs
      .filter((p) => activeMemberships.some((m) => m?.programId === p.id))
      .map((p) => p.institution?.name)
      .join(", ");

    const programName =
      programs.find((p) => activeMemberships.some((m) => m?.programId === p.id))
        ?.name ?? "N/A";

    return {
      ...user,
      role: Array.from(roleSet).join(", "),
      rawRoles: programMemberships
        .filter(
          (m): m is ProgramMembership & { programRole: ProgramRole } =>
            !!m?.programRole && !m.endDate
        )
        .map((m) => m.programRole),
      trainingProgram: trainingPrograms,
      institutionName,
      program: programName,
      jobTitle: user.jobTitle ?? undefined,
      status: user.eulaAcceptedDate ? "Active" : "Unregistered"
    } as ProgramUserWithRawRoles;
  };

  const usersUnique: ProgramUserWithRawRoles[] = uniqBy(users, "email")
    .map(enrichUser)
    .filter((user) =>
      selectedStatus === "" ? true : user.status === selectedStatus
    );

  const necessaryStatuses = [programsStatus, networkProgramsStatus];

  const anyApiIsLoading = [
    getUsersStatus,
    programsStatus,
    resendInvitationsStatus
  ].includes("loading");

  useEffect(() => {
    if (resendInvitationsStatus === "idle") return;

    if (resendInvitationsStatus === "loading") {
      dispatch(setToastStyle("info"));
      dispatch(setToastMessage(t("Resending invitations...")));
      dispatch(setToastAutoHideDuration(null));
      dispatch(setShowToast(true));
    }

    if (resendInvitationsStatus === "succeeded") {
      const { message, style } = resendSummary
        ? buildResendSummaryMessage(resendSummary)
        : {
            message: `Invitation has been re-sent successfully to ${successCount} ${
              successCount === 1 ? "person" : "people"
            }.`,
            style: "success" as const
          };
      dispatch(setToastStyle(style));
      dispatch(setToastMessage(t(message)));
      // Persist until dismissed — the result summary is easy to miss otherwise.
      dispatch(setToastAutoHideDuration(null));
      // Clear the selection so a follow-up action doesn't re-send to the same
      // (growing) list by accident. Does not affect the "+1 Year" flow.
      setSelectedUserIds([]);
    }

    if (resendInvitationsStatus === "failed") {
      dispatch(setToastStyle("error"));
      dispatch(
        setToastMessage(
          t(
            "An error ocurred re-sending the invitations. Please contact your administrator for further assistance."
          )
        )
      );
      dispatch(setToastAutoHideDuration(null));
    }
    dispatch(setShowToast(true));
  }, [resendInvitationsStatus, successCount, currentStep, resendSummary]);

  useEffect(() => {
    // Check if programs actually changed
    const programsChanged = 
      !prevProgramsRef.current || 
      prevProgramsRef.current.length !== programs?.length ||
      prevProgramsRef.current.map(p => p.id).sort().join(",") !== programs?.map(p => p.id).sort().join(",");
    
    // Check if other dependencies actually changed
    const selectedProgramIdChanged = prevSelectedProgramIdRef.current !== selectedProgramId;
    const selectedRoleChanged = prevSelectedRoleRef.current !== selectedRole;
    const refetchUsersChanged = prevRefetchUsersRef.current !== refetchUsers;
    const currentUserChanged = prevCurrentUserIdRef.current !== currentUser?.id;

    // Only proceed if something actually changed
    if (!programsChanged && !selectedProgramIdChanged && !selectedRoleChanged && !refetchUsersChanged && !currentUserChanged) {
      return;
    }

    // Update refs
    prevProgramsRef.current = programs || [];
    prevSelectedProgramIdRef.current = selectedProgramId;
    prevSelectedRoleRef.current = selectedRole;
    prevRefetchUsersRef.current = refetchUsers;
    prevCurrentUserIdRef.current = currentUser?.id;

    if (programs && programs.length > 0) {
      const { programMembershipsAsProgramAdmin } =
        getProgramMembershipsAsProgramAdmin(currentUser);  
      const idsOfProgramsUserAdmins = programMembershipsAsProgramAdmin?.map(
        (programMembership) => programMembership?.programId
      );
      const allUsersAdminedPrograms = programs?.filter((program) =>
        idsOfProgramsUserAdmins?.some(
          (adminedProgramId) => adminedProgramId === program.id
        )
      );
      setAllUsersAdminedPrograms(allUsersAdminedPrograms);
      const idsOfAllUsersAdminedPrograms = allUsersAdminedPrograms?.map(
        (program) => program.id
      );
      if (idsOfAllUsersAdminedPrograms.length === 1 && !selectedProgramId) {
        setSelectedProgramId(idsOfAllUsersAdminedPrograms[0]);
      }
      let programsToFetch = idsOfAllUsersAdminedPrograms;
      let roleToFetch = undefined;
      if (selectedProgramId) {
        programsToFetch = [selectedProgramId];
      }
      if (selectedRole !== "") {
        roleToFetch = selectedRole as ProgramRole;
      }
      if (programsToFetch && programsToFetch.length > 0) {
        getUsersByProgramIds(programsToFetch, roleToFetch, true);
      }
    }
    if (currentUser) {
      const activeProgramAdminedProgramIds =
        getActiveProgramAdminedProgramIds(currentUser);
      if (
        activeProgramAdminedProgramIds &&
        activeProgramAdminedProgramIds.length > 0
      ) {
        getNetworkProgramsByPrograms(activeProgramAdminedProgramIds);
      }
    }
  }, [selectedProgramId, selectedRole, refetchUsers, currentUser, programs]);

  useEffect(() => {
    const selectedUsers = selectedUserIds
      .map((userId) => usersUnique.find((user) => user.id === userId))
      .filter(Boolean)
      .filter((user: any) =>
        canBumpUser(user, trainingStages)
      ) as typeof usersUnique;

    dispatch(
      setModalContent(
        <Box
          sx={{ display: "flex", flexDirection: "column" }}
          alignItems="center"
          gap={3}
          padding={3}
        >
          <Box>
            You are updating the Clinical Year by{" "}
            <PgyDeltaInput
              setDeltaValue={setPgyBumpValue}
              currentDeltaValue={pgyBumpValue}
            />{" "}
            for {selectedUsers.length} people
          </Box>
          <Button
            variant="contained"
            size="small"
            disabled={!pgyBumpValue}
            onClick={() => {
              if (!pgyBumpValue) return;

              const plans = selectedUsers
                .map((user: any) =>
                  getPgyBumpPlan(user, trainingStages, pgyBumpValue)
                )
                .filter((p): p is NonNullable<typeof p> => p !== null);

              if (plans.length === 0) {
                dispatch(setToastStyle("error"));
                dispatch(
                  setToastMessage(
                    t(
                      "Clinical Year cannot be updated for any selected users."
                    )
                  )
                );
                dispatch(setShowToast(true));
                return;
              }

              applyPgyBumps(plans, (input) =>
                updateUser(input as any)
              )
                .then((result) => {
                  const successes =
                    result.yearInStageUpdated + result.pgyUpdated;
                  const expected =
                    plans.filter((p) => p.newYearInStage != null).length +
                    plans.filter((p) => p.newPgy != null).length;
                  const summary = {
                    fulfilled: successes,
                    rejected: Math.max(expected - successes, 0)
                  };
                  const skipNotes: string[] = [];
                  if (result.yearInStageSkipped.length > 0) {
                    skipNotes.push(
                      `${result.yearInStageSkipped.length} skipped: out of range`
                    );
                  }
                  if (result.pgyFailed.length > 0) {
                    skipNotes.push(
                      `${result.pgyFailed.length} failed`
                    );
                  }
                  const skippedSuffix =
                    skipNotes.length > 0 ? ` (${skipNotes.join("; ")})` : "";
                  showToastForBulkResult(
                    dispatch,
                    summary,
                    // Compare against field count (not user count): Fellowship
                    // updates 2 fields/user, so a full success must equal
                    // `expected` fields to be styled green rather than yellow.
                    expected,
                    t(
                      `You have successfully updated Clinical Year for ${
                        plans.length
                      } ${plans.length > 1 ? "users" : "user"}`
                    ),
                    t(
                      `Clinical Year updated for ${result.yearInStageUpdated + result.pgyUpdated} of ${expected} field(s) across ${plans.length} users${skippedSuffix}`
                    ),
                    t(
                      "An error occurred updating Clinical Year, please try again later"
                    )
                  );
                })
                .catch((err) => {
                  console.error(
                    "Error updating Clinical Year:",
                    err
                  );
                  dispatch(setToastStyle("error"));
                  dispatch(
                    setToastMessage(
                      t(
                        "An error occurred updating Clinical Year, please try again later"
                      )
                    )
                  );
                  dispatch(setShowToast(true));
                });
              setRefetchUsers(refetchUsers + 1);
              dispatch(setShowModal(false));
            }}
          >
            CONFIRM
          </Button>
        </Box>
      )
    );
    dispatch(setModalBoxStyle({ width: 444, minWidth: 444 }));
    if (pgyModalInvocations > 0) dispatch(setShowModal(true));
  }, [pgyModalInvocations, pgyBumpValue, trainingStages]);

  // SE-1723: re-fetch each selected user's CURRENT memberships when the dialog
  // opens (the table data can lag a membership edit), scoped to the PA's programs
  // (and the filtered program if one is selected). Falls back to table data on
  // error. A user can hold more than one here (e.g. Attending + Program Admin),
  // so they're shown as checkboxes and the PA picks which — nothing ends blindly.
  useEffect(() => {
    if (!paEndConfirmOpen) return;
    let cancelled = false;
    setPaLoadingMemberships(true);
    (async () => {
      // The PA's OWN administered programs — NOT all programs. A selected user
      // may belong to programs this PA doesn't administer; those must never show.
      const paProgramIds = new Set(
        (currentUser
          ? getActiveProgramAdminedProgramIds(currentUser) ?? []
          : []
        ).filter(Boolean)
      );
      const inScope = (m: ProgramMembership) =>
        !m.endDate &&
        !!m.programId &&
        paProgramIds.has(m.programId) &&
        (!selectedProgramId || m.programId === selectedProgramId);
      const mapMs = (m: ProgramMembership) => ({
        id: m.id,
        programId: m.programId ?? "",
        programRole: m.programRole,
        programName: programs.find((p) => p.id === m.programId)?.name ?? null
      });
      const result: Record<string, ReturnType<typeof mapMs>[]> = {};
      const ids = [...selectedUserIds];
      for (let i = 0; i < ids.length; i += 10) {
        await Promise.all(
          ids.slice(i, i + 10).map(async (uid) => {
            try {
              const u = await fetchUser(uid);
              const items = (u?.programMemberships?.items ??
                []) as (ProgramMembership | null)[];
              result[uid] = items
                .filter((m): m is ProgramMembership => !!m && inScope(m))
                .map(mapMs);
            } catch {
              const tu = usersUnique.find((x) => x.id === uid);
              const items = (tu?.programMemberships?.items ||
                []) as (ProgramMembership | null)[];
              result[uid] = items
                .filter((m): m is ProgramMembership => !!m && inScope(m))
                .map(mapMs);
            }
          })
        );
      }
      if (!cancelled) {
        setPaFresh(result);
        setPaLoadingMemberships(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paEndConfirmOpen]);

  const paKey = (userId: string, mId: string) => `${userId}:${mId}`;
  const paReviewUsers = selectedUserIds
    .map((id) => usersUnique.find((u) => u.id === id))
    .filter((u): u is (typeof usersUnique)[number] => !!u)
    .map((u) => ({
      userId: u.id,
      label:
        [u.firstName, u.lastName].filter(Boolean).join(" ") || u.email || u.id,
      memberships: paFresh[u.id] ?? []
    }));
  const paTotalChecked = paReviewUsers.reduce(
    (n, u) =>
      n +
      u.memberships.filter((m) => !paExcluded.has(paKey(u.userId, m.id))).length,
    0
  );
  const togglePaMembership = (userId: string, mId: string) => {
    const k = paKey(userId, mId);
    setPaExcluded((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  };

  const runPaEnd = async () => {
    const targets: BulkEndTarget[] = paReviewUsers
      .map((u) => ({
        userId: u.userId,
        userLabel: u.label,
        memberships: u.memberships
          .filter((m) => !paExcluded.has(paKey(u.userId, m.id)))
          .map((m) => ({
            id: m.id,
            programId: m.programId,
            programRole: m.programRole,
            programName: m.programName
          }))
      }))
      .filter((tgt) => tgt.memberships.length > 0);

    setPaEndConfirmOpen(false);
    setSelectedUserIds([]);
    setPaExcluded(new Set());
    setPaEndRunning(true);
    try {
      const outcome = await applyBulkEnd(targets, toEndDateIso(new Date()));
      const ok = outcome.succeeded.length;
      const floor = outcome.floorRejected.length;
      const scopedRefused = outcome.scopedRefused.length;
      const failed = outcome.failed.length;
      const parts = [`${ok} ended`];
      if (floor) parts.push(`${floor} couldn't be ended`);
      if (scopedRefused)
        parts.push(
          `${scopedRefused} skipped — end Coach / Rotation Director in Edit User`
        );
      if (failed) parts.push(`${failed} failed`);
      dispatch(
        setToastStyle(
          failed ? "error" : floor || scopedRefused ? "warning" : "success"
        )
      );
      dispatch(setToastMessage(parts.join(" · ")));
      dispatch(setToastAutoHideDuration(null));
      dispatch(setShowToast(true));
      setRefetchUsers((n) => n + 1);
    } finally {
      setPaEndRunning(false);
    }
  };

  if (necessaryStatuses.includes("loading")) {
    return <CircularProgress />;
  }

  if (necessaryStatuses.every((status) => status === "succeeded")) {
    return (
      <Grid
        sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}
        spacing={2}
        gap={2}
      >
        <Grid xs={12} pt={2}>
          <Grid xs={12} container spacing={2}>
            <Grid>
              <FormControl>
                <InputLabel shrink id="program-label">
                  {t("Program")}
                </InputLabel>
                <Select
                  labelId="program-label"
                  label={t("Programs")}
                  id="programSelection"
                  style={{ width: 300 }}
                  size="small"
                  displayEmpty
                  value={selectedProgramId || ""}
                  input={<OutlinedInput notched label={t("Programs")} />}
                  onChange={(event) => {
                    setSelectedProgramId(event.target.value);
                    setSelectedUserIds([]);
                  }}
                >
                  <MenuItem value={""} key={"no-program"}>
                    All
                  </MenuItem>
                  {alphabetizePrograms(
                    programsVisibleTo(allUsersAdminedPrograms ?? [], isSysAdmin),
                    true
                  ).map((program) => (
                    <MenuItem value={program.id} key={program.id}>
                      {/* TODO - The rest of this filter row (and other
                          elements) is still duplicated between the
                          ProgramAdmin and SysAdmin Users tables. The label
                          itself is now shared via `programLabel`. */}
                      {programLabel(program)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid>
              <FormControl>
                <InputLabel shrink id="role-label">
                  {t("Role")}
                </InputLabel>
                <Select
                  labelId="role-label"
                  id="roleSelection"
                  label={t("Role")}
                  style={{ width: 300 }}
                  size="small"
                  displayEmpty
                  value={selectedRole || ""}
                  input={<OutlinedInput notched label={t("Role")} />}
                  onChange={(event) => {
                    const value = event.target.value;
                    if (value === "") {
                      setSelectedRole(value);
                    } else {
                      setSelectedRole(value as ProgramRole);
                    }
                    setSelectedUserIds([]);
                  }}
                >
                  <MenuItem value="">All</MenuItem>
                  {ProgramRoleSelectFieldsNew?.map((roleSelectField) => (
                    <MenuItem
                      value={roleSelectField.value}
                      key={roleSelectField.value}
                    >
                      {roleSelectField.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid>
              <FormControl>
                <InputLabel shrink id="status-label">
                  {t("Status")}
                </InputLabel>
                <Select
                  labelId="status-label"
                  id="statusSelection"
                  label={t("Status")}
                  style={{ width: 300 }}
                  size="small"
                  displayEmpty
                  value={selectedStatus || ""}
                  input={<OutlinedInput notched label={t("Status")} />}
                  onChange={(event) => {
                    const value = event.target.value;
                    setSelectedStatus(value);
                    setSelectedUserIds([]);
                  }}
                >
                  <MenuItem value="">All</MenuItem>
                  {[
                    { value: "Active", label: "Active" },
                    { value: "Unregistered", label: "Unregistered" }
                  ]?.map((roleSelectField) => (
                    <MenuItem
                      value={roleSelectField.value}
                      key={roleSelectField.value}
                    >
                      {roleSelectField.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Grid>

        <Grid xs={12} container spacing={2}>
          <Grid>
            <Button
              variant="contained"
              disabled={anyApiIsLoading}
              size="small"
              startIcon={<PersonAdd />}
              onClick={() => navigate("/invitations/new")}
            >
              {t("NEW USER")}
            </Button>
          </Grid>
          {allow_bulk_invite && (
            <Grid>
              <Button
                variant="contained"
                disabled={anyApiIsLoading}
                size="small"
                startIcon={<GroupAdd />}
                onClick={() => navigate("/invitations/bulkupload")}
              >
                {t("BULK NEW USERS")}
              </Button>
            </Grid>
          )}
          <Grid>
            <LoadingButton
              disabled={selectedUserIds.length === 0 || anyApiIsLoading}
              variant="contained"
              size="small"
              startIcon={<ScheduleSendRounded />}
              loading={resendInvitationsStatus === "loading"}
              loadingPosition="start"
              onClick={() => {
                setResendConfirmOpen(true);
              }}
            >
              {t("RESEND INVITATION")}
            </LoadingButton>
          </Grid>

          <Grid>
            <Button
              disabled={selectedUserIds.length === 0 || anyApiIsLoading}
              variant="contained"
              size="small"
              onClick={() => {
                const selectedUsers = selectedUserIds
                  .map((userId) => usersUnique.find((user) => user.id === userId))
                  .filter(Boolean)
                  .filter((user: any) =>
                    canBumpUser(user, trainingStages)
                  ) as typeof usersUnique;

                if (!selectedUsers?.length) {
                  dispatch(
                    setToastMessage(
                      t(
                        "None of the selected users have a Clinical Year value set. Set one via Edit User before bumping."
                      )
                    )
                  );
                  dispatch(setToastStyle("error"));
                  dispatch(setShowToast(true));
                  return;
                }

                setPgyModalInvocations(pgyModalInvocations + 1);
              }}
            >
              {t("+1 Year")}
            </Button>
          </Grid>

          <Grid>
            <Button
              disabled={
                selectedUserIds.length === 0 || anyApiIsLoading || paEndRunning
              }
              variant="contained"
              size="small"
              startIcon={<EventBusyRounded />}
              onClick={() => {
                setPaExcluded(new Set());
                setPaEndConfirmOpen(true);
              }}
            >
              {t("END MEMBERSHIPS")}
            </Button>
          </Grid>

          {/* Create a modal to show that report generation has been initiated w/ email message */}
        </Grid>

        {(getUsersStatus === "failed" || error) && (
          <DataGridErrorOverlay
            errorMessage={UserNetworkErrors.FetchAllUsersError}
          />
        )}
        <Grid xs={12} sx={{ flex: 1, minHeight: 0, display: "flex" }}>
          <DataGridPremium
            sx={{
              flex: 1,
              minHeight: 0,
              [`& .${gridClasses.cell}`]: {
                py: 1,
                whiteSpace: "pre-wrap"
              }
            }}
            checkboxSelection
            rowSelectionModel={selectedUserIds}
            onRowSelectionModelChange={(selection) => {
              setSelectedUserIds(selection as string[]);
            }}
            disableVirtualization={disableVirtualization}
            aria-label="users-table-programadmins"
            rows={usersUnique || []}
            columns={columns}
            loading={anyApiIsLoading}
            localeText={{
              noRowsLabel: t(UserNotifications.NoUsers)
            }}
            getRowHeight={() => "auto"}
            onRowClick={handleRowClick}
          />
        </Grid>

        <Dialog
          open={resendConfirmOpen}
          onClose={() => setResendConfirmOpen(false)}
          aria-labelledby="resend-confirm-title"
        >
          <DialogTitle id="resend-confirm-title">
            {t("Resend invitations?")}
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              {t(
                `This will email an invitation to ${selectedUserIds.length} ${
                  selectedUserIds.length === 1 ? "user" : "users"
                }. They will each receive a registration email now. Only proceed if you intend to notify them.`
              )}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setResendConfirmOpen(false)}>
              {t("Cancel")}
            </Button>
            <LoadingButton
              variant="contained"
              startIcon={<ScheduleSendRounded />}
              loading={resendInvitationsStatus === "loading"}
              loadingPosition="start"
              onClick={() => {
                // Snapshot the IDs, then clear the selection immediately — the
                // grid is controlled by selectedUserIds, so this empties both the
                // checkboxes and the underlying state at once. No stale/hidden
                // selection can survive to be re-sent by a later action.
                const idsToSend = [...selectedUserIds];
                setResendConfirmOpen(false);
                setSelectedUserIds([]);
                bulkResendUserInvitations(idsToSend);
              }}
            >
              {t("Send")}
            </LoadingButton>
          </DialogActions>
        </Dialog>

        <Dialog
          open={paEndConfirmOpen}
          onClose={() => (paEndRunning ? undefined : setPaEndConfirmOpen(false))}
          aria-labelledby="pa-end-confirm-title"
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle id="pa-end-confirm-title">
            {t("End memberships?")}
          </DialogTitle>
          <DialogContent dividers>
            <DialogContentText sx={{ mb: 2 }}>
              {t(
                "Check the memberships to end below. Only the ones you check are ended, as of today — a user's other memberships are left untouched. This can't be undone; for an earlier end date, contact "
              )}
              <Link
                href={`mailto:help@simpl.org?subject=${encodeURIComponent(
                  "SIMPL Support- Change End Date of a Program Membership"
                )}`}
                underline="hover"
                target="_blank"
                rel="noopener noreferrer"
              >
                {t("SIMPL Support")}
              </Link>
              .
            </DialogContentText>
            {paLoadingMemberships ? (
              <Box display="flex" alignItems="center" gap={1} py={2}>
                <CircularProgress size={18} />
                <Typography variant="body2" color="text.secondary">
                  {t("Loading current memberships…")}
                </Typography>
              </Box>
            ) : (
              <Stack
                spacing={1}
                divider={
                  <Box
                    sx={{ borderBottom: "1px solid", borderColor: "divider" }}
                  />
                }
              >
              {paReviewUsers.map((u) => (
                <Box key={u.userId}>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Typography variant="subtitle2">{u.label}</Typography>
                    <Tooltip title={t("Remove user from list")}>
                      <IconButton
                        size="small"
                        aria-label={t("Remove user from list")}
                        onClick={() =>
                          setSelectedUserIds((prev) =>
                            prev.filter((id) => id !== u.userId)
                          )
                        }
                      >
                        <CloseRounded fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  {u.memberships.length === 0 ? (
                    <Typography variant="caption" color="text.secondary">
                      {t("No active memberships")}
                    </Typography>
                  ) : (
                    <FormGroup>
                      {u.memberships.map((m) => (
                        <FormControlLabel
                          key={m.id}
                          control={
                            <Checkbox
                              size="small"
                              checked={!paExcluded.has(paKey(u.userId, m.id))}
                              onChange={() =>
                                togglePaMembership(u.userId, m.id)
                              }
                            />
                          }
                          label={
                            <Typography variant="body2">
                              {[m.programName, m.programRole]
                                .filter(Boolean)
                                .join(" · ")}
                            </Typography>
                          }
                        />
                      ))}
                    </FormGroup>
                  )}
                </Box>
              ))}
              </Stack>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setPaEndConfirmOpen(false)}
              disabled={paEndRunning}
            >
              {t("Cancel")}
            </Button>
            <LoadingButton
              variant="contained"
              loading={paEndRunning}
              disabled={paTotalChecked === 0}
              startIcon={<EventBusyRounded />}
              loadingPosition="start"
              onClick={runPaEnd}
            >
              {t(
                `End ${paTotalChecked} membership${
                  paTotalChecked === 1 ? "" : "s"
                }`
              )}
            </LoadingButton>
          </DialogActions>
        </Dialog>
      </Grid>
    );
  }

  return null;
};
